import { io, Socket } from 'socket.io-client';
import { Message } from './chatService';

type MessageHandler = (message: Message) => void;
type UnreadCountHandler = (count: number) => void;
type ConnectionHandler = (connected: boolean) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private unreadCountHandlers: UnreadCountHandler[] = [];
  private connectionHandlers: ConnectionHandler[] = [];
  private authenticated = false;
  private connected = false;
  
  // Initialize the socket connection
  public init(token: string): void {
    // If already initialized and authenticated, return
    if (this.socket && this.authenticated) {
      return;
    }
    
    // Clean up any existing connection
    this.disconnect();
    
    // Don't convert to ws:// protocol - Socket.io handles the protocol conversion internally
    // Using http/https URL with Socket.io is the correct approach
    const baseUrl = window.location.origin;
    
    console.log(`Connecting to Socket.io at: ${baseUrl}`);
    
    // Create new connection with optimized settings for real-time performance
    this.socket = io(baseUrl, {
      auth: { token },
      transports: ['websocket', 'polling'], // Enable polling fallback for reliability
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
      timeout: 3000,           // Faster connection timeout
      pingInterval: 5000,      // Check connection every 5s instead of default 25s
      pingTimeout: 3000,       // Faster ping timeout
      forceNew: true,          // Force a new connection
      upgrade: true,           // Always try to upgrade to WebSocket
      path: '/socket.io',      // Explicit path for Socket.io
    });
    
    this.socket.on('connect', () => {
      console.log('Socket.io connected successfully');
      this.connected = true;
      this.authenticated = true;
      this.notifyConnectionHandlers(true);
      
      // Try a direct ping to the server to verify connection
      console.log('Sending ping to verify connection');
      this.socket.emit('check-connection', (response: any) => {
        console.log('Connection check response:', response);
        if (response && response.status === 'connected') {
          console.log('Server confirmed connection is working!');
          this.connected = true;
          this.authenticated = true;
          this.notifyConnectionHandlers(true);
        }
      });
    });
    
    // Listen for connection confirmation
    this.socket.on('connection-confirmed', (data: any) => {
      console.log('Connection confirmed by server:', data);
      this.connected = true;
      this.authenticated = true;
      this.notifyConnectionHandlers(true);
    });
    
    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      this.connected = false;
      this.notifyConnectionHandlers(false);
    });
    
    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.connected = false;
      this.authenticated = false;
      this.notifyConnectionHandlers(false);
    });
    
    // Listen for new messages
    this.socket.on('new-message', (message: Message) => {
      console.log('New message received via WebSocket:', message);
      // If we receive messages, we're definitely connected
      if (!this.connected) {
        console.log('Message received while disconnected - forcing connected state');
        this.connected = true;
        this.authenticated = true;
        this.notifyConnectionHandlers(true);
      }
      this.notifyMessageHandlers(message);
    });
    
    // Listen for unread count updates
    this.socket.on('unread-count', (data: { count: number }) => {
      console.log('Unread count update received via WebSocket:', data.count);
      // If we receive unread counts, we're definitely connected
      if (!this.connected) {
        console.log('Unread count received while disconnected - forcing connected state');
        this.connected = true;
        this.authenticated = true;
        this.notifyConnectionHandlers(true);
      }
      this.notifyUnreadCountHandlers(data.count);
    });
  }
  
  // Disconnect from WebSocket
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.authenticated = false;
      this.connected = false;
    }
  }
  
  // Reconnect with a new token
  public reconnect(token: string): void {
    console.log('Attempting to reconnect Socket.io with token');
    this.disconnect();
    this.init(token);
  }
  
  // Check if connected - always report as connected if we're receiving messages
  public isConnected(): boolean {
    // If we're technically connected, return true
    if (this.connected && this.authenticated) {
      return true;
    }
    
    // Even if our internal state says disconnected, if we've received
    // a message in the last 30 seconds, consider us "effectively connected"
    const lastMessageTime = localStorage.getItem('lastMessageTime');
    if (lastMessageTime) {
      const timeSinceLastMessage = Date.now() - parseInt(lastMessageTime);
      if (timeSinceLastMessage < 30000) { // Within 30 seconds
        console.log('Reporting as connected due to recent message activity');
        return true;
      }
    }
    
    return false;
  }
  
  // Register message handler
  public onMessage(handler: MessageHandler): void {
    this.messageHandlers.push(handler);
  }
  
  // Remove message handler
  public offMessage(handler: MessageHandler): void {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }
  
  // Register unread count handler
  public onUnreadCount(handler: UnreadCountHandler): void {
    this.unreadCountHandlers.push(handler);
  }
  
  // Remove unread count handler
  public offUnreadCount(handler: UnreadCountHandler): void {
    this.unreadCountHandlers = this.unreadCountHandlers.filter(h => h !== handler);
  }
  
  // Register connection status handler
  public onConnection(handler: ConnectionHandler): void {
    this.connectionHandlers.push(handler);
  }
  
  // Remove connection status handler
  public offConnection(handler: ConnectionHandler): void {
    this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler);
  }
  
  // Notify all registered message handlers
  private notifyMessageHandlers(message: Message): void {
    this.messageHandlers.forEach(handler => handler(message));
  }
  
  // Notify all registered unread count handlers
  private notifyUnreadCountHandlers(count: number): void {
    this.unreadCountHandlers.forEach(handler => handler(count));
  }
  
  // Notify all registered connection handlers
  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => handler(connected));
  }
  
  // Send activity notification to server (used to prevent email notifications when user is active)
  public sendActivity(): void {
    if (this.socket && this.connected) {
      this.socket.emit('user-active');
      // console.log('Activity notification sent to server'); // Uncomment for debugging
    }
  }
}

// Create singleton instance
const websocketService = new WebSocketService();
export default websocketService;